import axios from 'axios';
import { getRequiredVerificationRoute } from '../utils/verification';

import {
  AUTH_USER_KEY,
  clearStoredAuth,
  getStoredAuthToken,
  getStoredUser,
  getApiErrorCode,
  getStoredRefreshToken,
  normalizeAuthResponse,
  sanitizeDisplayText,
  assertApiSuccess,
  storeAuthToken,
  storeRefreshToken,
} from '../utils/apiResponseUtils';
import { getEnvValue } from './envConfig';

const normalizeBaseUrl = (url) => (url || '').replace(/\/+$/, '');
const normalizePath = (path) => (path.startsWith('/') ? path : `/${path}`);

const LOCAL_API_BASE_URL = 'http://localhost:8080';

export const API_BASE_URL = normalizeBaseUrl(
  getEnvValue('REACT_APP_API_BASE_URL', LOCAL_API_BASE_URL)
);
export const AUTH_BASE_URL = normalizeBaseUrl(
  getEnvValue('REACT_APP_AUTH_BASE_URL', API_BASE_URL.replace(/\/api$/, ''))
);

const buildUrl = (baseUrl, path) => `${baseUrl}${normalizePath(path)}`;
const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/signup',
  '/auth/social-login',
  '/auth/send-otp',
  '/auth/verify-otp',
  '/auth/login-with-otp',
  '/auth/create-account-with-otp',
  '/auth/password-reset/request',
  '/auth/password-reset/confirm',
];

const isPublicAuthEndpoint = (requestUrl = '') => {
  const normalizedRequestUrl = normalizePath(String(requestUrl || ''));

  return PUBLIC_AUTH_ENDPOINTS.some((endpoint) => (
    normalizedRequestUrl === endpoint ||
    normalizedRequestUrl.endsWith(endpoint)
  ));
};

const removeAuthorizationHeader = (headers = {}) => {
  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
    return headers;
  }

  delete headers.Authorization;
  delete headers.authorization;
  return headers;
};

const isSafeBearerToken = (token) => (
  typeof token === 'string' &&
  token.length <= 4096 &&
  !/[\r\n]/.test(token) &&
  /^[A-Za-z0-9._~+/=-]+$/.test(token)
);

const getLoginRedirectPath = (requestUrl = '') => {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const normalizedRequestUrl = String(requestUrl || '');
  let storedRole = '';

  try {
    storedRole = sanitizeDisplayText(JSON.parse(sessionStorage.getItem(AUTH_USER_KEY) || '{}')?.role || '').toLowerCase();
  } catch {
    storedRole = '';
  }

  if (normalizedRequestUrl.includes('/admin/') || currentPath.startsWith('/admin') || storedRole === 'admin') {
    return '/admin/login';
  }

  if (normalizedRequestUrl.includes('/driver/') || currentPath.startsWith('/driver') || storedRole === 'driver') {
    return '/driver/login';
  }

  return '/login';
};

const shouldRetryAgainstAuthBase = (error, retryStatuses = [404]) => {
  if (!API_BASE_URL.endsWith('/api') || AUTH_BASE_URL === API_BASE_URL) {
    return false;
  }

  return error?.response?.status === 404;
};

export const postToAuthEndpoint = async (path, data, config = {}) => {
  const {
    retryAuthBaseOnStatuses,
    skipAuth,
    ...axiosConfig
  } = config;
  const shouldSkipAuth = skipAuth !== false && isPublicAuthEndpoint(path);
  const requestConfig = {
    ...axiosConfig,
    skipAuth: shouldSkipAuth,
    headers: {
      'Content-Type': 'application/json',
      ...(axiosConfig.headers || {}),
    },
  };

  if (shouldSkipAuth) {
    requestConfig.headers = removeAuthorizationHeader(requestConfig.headers);
  }

  try {
    const response = await axios.post(buildUrl(API_BASE_URL, path), data, requestConfig);
    assertApiSuccess(response);
    return response;
  } catch (error) {
    if (!shouldRetryAgainstAuthBase(error, retryAuthBaseOnStatuses || [404])) {
      throw error;
    }

    const response = await axios.post(buildUrl(AUTH_BASE_URL, path), data, requestConfig);
    assertApiSuccess(response);
    return response;
  }
};

let verificationHandler = () => {};
let getCurrentUser = getStoredUser;
export const configureVerification = (handler, getUser) => {
  verificationHandler = handler;
  getCurrentUser = getUser;
};

export const isOrderRequest = (config) => {
  const path = String(config.url || '').split('?')[0].replace(/\/+$/, '');
  return /\/(payment\/(quotes|checkouts(?:\/.*)?|wallet(?:\/.*)?|deliveries\/.*)|account(?:\/closure-preview)?)$/.test(path) || /\/driver\/(orders(?:\/.*)?|location)$/.test(path) ||
    (String(config.method || 'get').toLowerCase() === 'post' &&
      /\/(delivery\/create|payment\/(?:create-order|verify))$/.test(path));
};

let refreshPromise = null;
let sessionExpiredHandler = (url) => { window.location.href = getLoginRedirectPath(url); };
export const configureSessionExpiry = (handler) => { sessionExpiredHandler = handler; };
const expireSession = (url) => { clearStoredAuth(); sessionExpiredHandler(url); };

const refreshAuthToken = async () => {
  const refreshToken = getStoredRefreshToken();

  if (!isSafeBearerToken(refreshToken)) {
    throw new Error('No refresh token available');
  }

  const response = await postToAuthEndpoint(
    '/auth/refresh',
    { refreshToken },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
      timeout: 10000,
    }
  );

  const authData = normalizeAuthResponse(response.data);

  if (!authData.token) {
    throw new Error('Token refresh failed');
  }

  storeAuthToken(authData.token);
  if (authData.refreshToken) {
    storeRefreshToken(authData.refreshToken);
  }
  return authData.token;
};

const commerceAccountMatches=config=>{
  if(!config?.commerceOwner)return true;
  const user=getCurrentUser();return String(user?.id??user?.customerId??user?.userId??'')===config.commerceOwner;
};
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
  (config) => {
    if(!commerceAccountMatches(config))return Promise.reject(new Error('Your account changed. Check the existing payment from its original account.'));
    const skipAuth = config.skipAuth || isPublicAuthEndpoint(config.url);

    const verificationRoute = getRequiredVerificationRoute(getCurrentUser());
    if (isOrderRequest(config) && verificationRoute) {
      const code = verificationRoute.endsWith('verify-email') ? 'EMAIL_NOT_VERIFIED' : 'PHONE_NOT_VERIFIED';
      verificationHandler(code);
      const error = new Error('Please complete account verification before continuing.');
      error.response = { status: 403, data: { code } };
      error.config = config;
      return Promise.reject(error);
    }

    if (skipAuth) {
      config.headers = removeAuthorizationHeader(config.headers || {});
      return config;
    }

    const token = getStoredAuthToken();
    if (isSafeBearerToken(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => {
    if(!commerceAccountMatches(response.config))return Promise.reject(new Error('Your account changed. Check the original payment account.'));
    const code = getApiErrorCode({ response });
    if (response.data?.success === false && ['EMAIL_NOT_VERIFIED', 'PHONE_NOT_VERIFIED'].includes(code)) {
      verificationHandler(code);
      const error = new Error('Please complete account verification before continuing.');
      error.response = response;
      error.config = response.config;
      return Promise.reject(error);
    }
    assertApiSuccess(response);
    return response;
  },
  async (error) => {
    if(!commerceAccountMatches(error.config))return Promise.reject(error);
    const code = getApiErrorCode(error);
    // Trip-code rejection is an action error, even when the backend uses HTTP 401.
    if (code === 'INVALID_OTP') return Promise.reject(error);
    if (['EMAIL_NOT_VERIFIED', 'PHONE_NOT_VERIFIED'].includes(code)) {
      verificationHandler(code);
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest?._retry && !isPublicAuthEndpoint(originalRequest.url)) {
      expireSession(originalRequest.url);
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.skipSessionRefresh &&
      !originalRequest.skipAuth &&
      !isPublicAuthEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;

      try {
        refreshPromise = refreshPromise || refreshAuthToken();
        const newToken = await refreshPromise;
        refreshPromise = null;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        refreshPromise = null;
        expireSession(originalRequest.url);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
